import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  Banknote,
  Bookmark,
  ChevronUp,
  Inbox,
  Minus,
  Plus,
  Printer,
  Search,
  ShoppingCart,
  Trash2,
  User,
  X,
} from 'lucide-react';
import {
  computeTotals,
  formatMoney,
  formatShopTime,
  relativeTime,
  type CartLineInput,
  type Discount,
  type OpenTicket,
} from '@bakery/shared';

import { api, type NotifyResult, type ReceiptPayload, type StockShortfallRow } from '../lib/api.js';
import { printReceipt } from '../lib/printing.js';
import { useVerticalSwipe } from '../lib/swipe.js';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Field,
  Input,
  Modal,
  MoneyInput,
  Select,
  Skeleton,
  cx,
  useToast,
} from '../components/ui.js';

interface CartLine extends CartLineInput {
  key: string;
}

const PAYMENT_METHODS = [
  { value: 'cash', label: 'Cash' },
  { value: 'gcash', label: 'GCash' },
  { value: 'maya', label: 'Maya' },
  { value: 'card', label: 'Card' },
  { value: 'bank_transfer', label: 'Bank transfer' },
] as const;

export function RegisterScreen() {
  const queryClient = useQueryClient();
  const toast = useToast();

  const [categoryId, setCategoryId] = useState<string | undefined>();
  const [search, setSearch] = useState('');
  const [cart, setCart] = useState<CartLine[]>([]);
  const [discountId, setDiscountId] = useState<string>('');
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [customerName, setCustomerName] = useState<string | null>(null);
  const [payOpen, setPayOpen] = useState(false);
  const [customerPickerOpen, setCustomerPickerOpen] = useState(false);
  const [parkOpen, setParkOpen] = useState(false);
  /* Phone only: the cart sheet starts collapsed so the grid has the screen. */
  const [cartOpen, setCartOpen] = useState(false);
  /*
   * Swipe the sheet up and down, not only the chevron.
   *
   * Bound to the whole sheet rather than a drag handle: the collapsed bar is
   * about 57px tall and mostly occupied by the total and the Charge button, so
   * a handle inside it would be a very small target held at arm's length over a
   * counter. The hook ignores gestures that start on a control, so Charge and
   * the quantity steppers still behave as buttons.
   */
  const cartSwipe = useVerticalSwipe({
    onSwipeUp: () => setCartOpen(true),
    onSwipeDown: () => setCartOpen(false),
  });
  const [ticketsOpen, setTicketsOpen] = useState(false);
  /**
   * Set while the cart came from a parked ticket. Charging then settles that
   * ticket rather than creating a second sale beside it.
   */
  const [resumedTicket, setResumedTicket] = useState<{ id: string; name: string } | null>(null);

  const tickets = useQuery({
    queryKey: ['tickets'],
    queryFn: api.tickets.list,
    refetchInterval: 30_000,
  });

  const categories = useQuery({ queryKey: ['categories'], queryFn: api.catalog.categories });
  const products = useQuery({
    queryKey: ['products', categoryId, search],
    queryFn: () => api.catalog.products({ categoryId, search: search || undefined }),
  });
  // Wrapped, not passed by reference: react-query calls the function with its
  // own context object, which as a first argument would read as
  // `includeInactive: true` and quietly offer retired discounts at the till.
  const discounts = useQuery({ queryKey: ['discounts'], queryFn: () => api.catalog.discounts() });
  const statuses = useQuery({ queryKey: ['order-statuses'], queryFn: () => api.orders.statuses(true) });

  // Falls back to the server's own default if the shop has hidden "Completed".
  const completedStatusId = statuses.data?.find((status) => status.systemKey === 'completed')?.id;

  const selectedDiscount: Discount | null =
    discounts.data?.find((discount) => discount.id === discountId) ?? null;

  // Totals are computed with the same function the server uses to commit them,
  // so what the cashier reads on screen is what the receipt will say.
  const totals = useMemo(
    () => computeTotals({ lines: cart, discount: selectedDiscount }),
    [cart, selectedDiscount],
  );

  /*
   * Confirming the tap landed.
   *
   * On a phone the cart sits below the product grid and is usually off-screen,
   * so adding an item changed nothing the cashier could see. The only feedback
   * was a 2% press shrink, which is indistinguishable from a tap the screen
   * missed — so the natural response is to tap again, and the natural result is
   * two of something on the bill. Hence two signals: a flash on the card that
   * says the tap registered, and a running count that says what is on the bill
   * without scrolling down to look.
   */
  const [flashed, setFlashed] = useState<string | null>(null);
  const flashTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => clearTimeout(flashTimer.current ?? undefined), []);

  const qtyInCart = useMemo(() => {
    const counts = new Map<string, number>();
    for (const line of cart) {
      // A line can be an open item with no product behind it; those belong to
      // no card, and counting them together under one null key would be wrong.
      if (!line.productId) continue;
      counts.set(line.productId, (counts.get(line.productId) ?? 0) + line.qty);
    }
    return counts;
  }, [cart]);

  function addToCart(product: { id: string; name: string; price: number; computedCost: number | null }) {
    if (flashTimer.current) clearTimeout(flashTimer.current);
    setFlashed(product.id);
    flashTimer.current = setTimeout(() => setFlashed(null), 400);

    setCart((current) => {
      const existing = current.find((line) => line.productId === product.id);
      if (existing) {
        return current.map((line) =>
          line.key === existing.key ? { ...line, qty: line.qty + 1 } : line,
        );
      }
      return [
        ...current,
        {
          key: `${product.id}-${Date.now()}`,
          productId: product.id,
          name: product.name,
          qty: 1,
          unitPrice: product.price,
          costPerUnit: product.computedCost ?? 0,
        },
      ];
    });
  }

  const setQty = (key: string, qty: number) =>
    setCart((current) =>
      qty <= 0 ? current.filter((line) => line.key !== key) : current.map((l) => (l.key === key ? { ...l, qty } : l)),
    );

  function clearCart() {
    setCart([]);
    setDiscountId('');
    setCustomerId(null);
    setCustomerName(null);
    setResumedTicket(null);
  }

  /** Load a parked ticket back into the cart, replacing whatever is there. */
  const resume = useMutation({
    mutationFn: (ticketId: string) => api.tickets.get(ticketId),
    onSuccess: (ticket) => {
      setCart(
        ticket.lines.map((line, index) => ({
          key: `${line.productId ?? 'custom'}-${index}-${Date.now()}`,
          productId: line.productId,
          name: line.nameSnapshot,
          qty: line.qty,
          // The parked price is what the customer was quoted; it stands even if
          // the catalog has been repriced since.
          unitPrice: line.unitPrice,
          modifiers: line.modifiers,
          discountAmount: line.discountAmount,
          notes: line.notes,
        })),
      );
      setCustomerId(ticket.customerId);
      setCustomerName(ticket.customerNameSnapshot);
      setResumedTicket({ id: ticket.id, name: ticket.name });
      setTicketsOpen(false);

      if (ticket.hasStaleLines) {
        toast.push({
          tone: 'warn',
          title: 'Prices have changed since this was parked',
          body: 'The ticket keeps what the customer was quoted. Re-add an item to use the current price.',
        });
      }
    },
    onError: (error: Error) => toast.push({ tone: 'danger', title: 'Could not open the ticket', body: error.message }),
  });

  const park = useMutation({
    mutationFn: (name: string) => {
      const lines = cart.map((line) => ({
        productId: line.productId,
        name: line.name,
        qty: line.qty,
        unitPrice: line.unitPrice,
        notes: line.notes,
      }));

      // Re-parking a resumed ticket updates it in place rather than leaving a
      // duplicate behind on the list.
      return resumedTicket
        ? api.tickets.update(resumedTicket.id, { name, lines, customerId, customerName })
        : api.tickets.park({ name, lines, customerId, customerName });
    },
    onSuccess: (ticket) => {
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      toast.push({ tone: 'ok', title: `Parked as "${ticket.name}"`, body: `${formatMoney(ticket.total)} held` });
      setParkOpen(false);
      clearCart();
    },
    onError: (error: Error) => toast.push({ tone: 'danger', title: 'Could not park', body: error.message }),
  });

  const discard = useMutation({
    mutationFn: (ticketId: string) => api.tickets.discard(ticketId),
    onSuccess: (_result, ticketId) => {
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      if (resumedTicket?.id === ticketId) clearCart();
      toast.push({ tone: 'ok', title: 'Ticket discarded' });
    },
  });

  /**
   * Run once, as the payment screen opens.
   *
   * Not on every cart change: a cashier adding items does not need a warning
   * flickering at them, and the moment that matters is the one before money
   * changes hands. Deliberately not a gate — the result is shown, and the
   * cashier decides. See the note in logic/availability.ts.
   */
  const stockCheck = useMutation({
    mutationFn: () =>
      api.inventory.check(
        cart.map((line) => ({ productId: line.productId, name: line.name, qty: line.qty })),
      ),
  });

  function openPayment() {
    stockCheck.reset();
    setPayOpen(true);
    // Fire and forget: a failed check must never stand between a customer and
    // paying, so the payment screen opens either way.
    stockCheck.mutate();
  }

  const checkout = useMutation({
    mutationFn: async (input: { method: string; tendered: number; reference: string | null }) => {
      /*
       * A resumed ticket settles through its own endpoint, which allocates the
       * order number, consumes the stock and removes the ticket in one
       * transaction. Going through the ordinary order path would leave the
       * ticket sitting on the list, chargeable a second time.
       */
      if (resumedTicket) {
        return api.tickets.checkout(resumedTicket.id, {
          method: input.method,
          tendered: input.method === 'cash' ? input.tendered : null,
          reference: input.reference,
          discountId: discountId || null,
        });
      }

      const result = await api.orders.create({
        customerId,
        customerName,
        fulfillment: 'pickup',
        source: 'pos',
        // Goods handed over across the counter are finished business, so the
        // sale closes immediately instead of queueing on the orders board.
        statusId: completedStatusId,
        discountId: discountId || null,
        lines: cart.map((line) => ({
          productId: line.productId,
          qty: line.qty,
          unitPrice: line.unitPrice,
        })),
        payments: [
          {
            method: input.method,
            amount: totals.total,
            tendered: input.method === 'cash' ? input.tendered : null,
            reference: input.reference,
          },
        ],
        // A counter sale that is already paid should not also text the customer
        // a request for payment.
        notify: false,
      });

      /*
       * The sale is already committed at this point. Numbering the receipt is a
       * follow-on, and it must never be able to fail the sale retroactively.
       *
       * It could, and did: `markPrinted` was a bare server call, so offline it
       * threw *after* the order had been written to this device. The till showed
       * "Sale not completed" for a sale that had completed, and a cashier would
       * ring it up a second time — a duplicate sale, with the stock taken twice.
       *
       * Offline the local path numbers the receipt as part of the sale, so there
       * is usually one already. When there is not, a failure here costs the slip,
       * not the sale, and the receipt can be printed later from Orders.
       */
      if (result.receipt) return { ...result, receipt: result.receipt };

      try {
        const receipt = await api.orders.markPrinted(result.order.id);
        return { ...result, receipt };
      } catch {
        return { ...result, receipt: null };
      }
    },
    onSuccess: async ({ order, receipt }) => {
      if (receipt) await printReceipt(receipt);
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      queryClient.invalidateQueries({ queryKey: ['analytics'] });
      queryClient.invalidateQueries({ queryKey: ['stock-alerts'] });
      // The ticket is gone server-side once it is charged; drop it from the list.
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      toast.push({
        tone: receipt ? 'ok' : 'warn',
        title: `${order.orderNo} rung up`,
        body: receipt
          ? `${formatMoney(order.total)} — receipt ${receipt.receiptNo}`
          : `${formatMoney(order.total)} taken. The receipt could not be printed — reprint it from Orders.`,
      });
      setPayOpen(false);
      clearCart();
    },
    onError: (error: Error) => toast.push({ tone: 'danger', title: 'Sale not completed', body: error.message }),
  });

  return (
    <div className="flex h-full flex-col lg:flex-row">
      {/* Product grid ----------------------------------------------------- */}
      {/*
        `min-h-0` matters as much as `min-w-0`: without it a flex item's
        automatic minimum size is its content, so the product grid refuses to
        shrink, and on a phone it squeezes the cart — and the Charge button —
        off the bottom of the screen instead of scrolling inside itself.
      */}
      <section className="flex min-h-0 min-w-0 flex-1 flex-col">
        <div className="sticky top-0 z-10 space-y-3 border-b border-[var(--color-line)] bg-[var(--color-canvas)] px-4 py-3">
          <div className="relative">
            <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-[var(--color-ink-faint)]" />
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search the menu…"
              className="pl-9"
            />
          </div>

          <div className="flex gap-1.5 overflow-x-auto pb-1">
            <CategoryChip active={!categoryId} onClick={() => setCategoryId(undefined)}>
              All
            </CategoryChip>
            {categories.data?.map((category) => (
              <CategoryChip
                key={category.id}
                active={categoryId === category.id}
                color={category.color}
                onClick={() => setCategoryId(category.id)}
              >
                {category.name}
              </CategoryChip>
            ))}
          </div>
        </div>

        {/* pb-20 on a phone: the collapsed cart bar floats over this, and the
            last row of products has to be scrollable clear of it. */}
        <div className="grid flex-1 auto-rows-min grid-cols-2 gap-2.5 overflow-y-auto p-4 pb-20 sm:grid-cols-3 lg:pb-4 xl:grid-cols-4">
          {products.isLoading &&
            Array.from({ length: 8 }).map((_, index) => <Skeleton key={index} className="h-28" />)}

          {products.data?.map((product) => (
            <button
              key={product.id}
              onClick={() => addToCart(product)}
              className={cx(
                'card relative flex flex-col justify-between p-3 text-left transition active:scale-95',
                'hover:border-crust-400 hover:elevation-raised',
                flashed === product.id && 'tap-confirm',
              )}
            >
              {(qtyInCart.get(product.id) ?? 0) > 0 && (
                <span
                  className="tabular absolute -top-1.5 -right-1.5 z-10 grid size-6 place-items-center rounded-full bg-crust-600 text-[11px] font-semibold text-white shadow"
                  aria-label={`${qtyInCart.get(product.id)} in the current sale`}
                >
                  {qtyInCart.get(product.id)}
                </span>
              )}
              <div className="min-w-0">
                <p className="line-clamp-2 text-sm leading-snug font-medium">{product.name}</p>
                {product.category && (
                  <p className="mt-0.5 truncate text-[11px] text-[var(--color-ink-faint)]">{product.category.name}</p>
                )}
              </div>
              <div className="mt-3 flex items-end justify-between gap-2">
                <span className="tabular font-display text-base font-semibold text-crust-700">
                  {formatMoney(product.price, { compactWhole: true })}
                </span>
                {typeof product.marginPercent === 'number' && (
                  <span className="tabular text-[11px] text-[var(--color-ink-faint)]">{product.marginPercent}%</span>
                )}
              </div>
            </button>
          ))}

          {products.data?.length === 0 && (
            <div className="col-span-full">
              <EmptyState
                icon={<Search className="size-8" />}
                title="Nothing matches"
                description="Try a different search, or clear the category filter."
              />
            </div>
          )}
        </div>
      </section>

      {/* Cart ------------------------------------------------------------- */}
      {/*
        A sheet on a phone, a column from `lg` up.

        Splitting the screen between the grid and the cart — which is what a
        fixed height cap did — starves both: too few products visible to sell
        from, and a line list too short to read. A phone only has room for one
        of them at a time, so the cart collapses to a summary bar and the grid
        gets the whole screen until someone asks for it.

        Fixed rather than in the flow, so opening it covers the grid instead of
        shrinking it, and the summary bar cannot be pushed off the bottom by a
        long cart — which was the original bug.
      */}
      <aside
        className={cx(
          'flex flex-col border-[var(--color-line)] bg-[var(--color-surface)]',
          // 72vh, not more: a strip of the grid stays visible behind the open
          // sheet, so it reads as something covering the screen temporarily
          // rather than as a screen you have navigated to.
          'fixed inset-x-0 bottom-tabbar z-30 max-h-[72vh] rounded-t-[var(--radius-lg)] border-t elevation-raised',
          'lg:static lg:z-auto lg:max-h-none lg:w-[400px] lg:shrink-0 lg:rounded-none lg:border-t-0 lg:border-l lg:shadow-none',
        )}
      >
        {/*
          The drag handle.

          A separate strip rather than the whole sheet, for two reasons that both
          broke the first attempt. The sheet contains scrolling lists, and the
          browser claims a vertical drag over those for scrolling — it fires
          `pointercancel` and the gesture is gone before it ends. And the bar
          below is almost entirely buttons, which the tracker deliberately
          ignores so a slip on Charge stays a press on Charge. `touch-none` is
          the other half: without it the browser still takes the gesture here.
        */}
        <div {...cartSwipe} className="touch-none px-3 pt-2 pb-1 lg:hidden">
          <div className="mx-auto h-1 w-9 rounded-full bg-[var(--color-line-strong)]" aria-hidden />
        </div>

        {/*
          The collapsed bar. Phone only, and the one thing always on screen:
          what is on the bill, and the way to charge it.
        */}
        <div className="flex items-stretch gap-2 px-3 pt-1 pb-2 lg:hidden">
          <button
            onClick={() => setCartOpen((open) => !open)}
            aria-expanded={cartOpen}
            className="flex min-w-0 flex-1 items-center gap-2 rounded-[var(--radius-md)] px-2 py-1.5 text-left hover:bg-[var(--color-surface-sunken)]"
          >
            <ShoppingCart className="size-4 shrink-0 text-[var(--color-ink-soft)]" />
            <span className="min-w-0 flex-1 truncate text-sm font-medium">
              {totals.itemCount === 0
                ? 'No items yet'
                : `${totals.itemCount} item${totals.itemCount === 1 ? '' : 's'}`}
              {resumedTicket && ` · ${resumedTicket.name}`}
            </span>
            <span className="tabular font-display text-base font-bold text-crust-700">
              {formatMoney(totals.total)}
            </span>
            <ChevronUp
              className={cx('size-4 shrink-0 text-[var(--color-ink-faint)] transition-transform', cartOpen && 'rotate-180')}
            />
          </button>
          <Button
            variant="primary"
            disabled={cart.length === 0}
            icon={<Banknote className="size-5" />}
            onClick={openPayment}
            className="shrink-0"
          >
            Charge
          </Button>
        </div>

        {/* Everything below is the cart proper: hidden on a phone until asked for. */}
        <div className={cx('flex min-h-0 flex-1 flex-col', !cartOpen && 'hidden lg:flex')}>
        <header className="flex items-center justify-between border-b border-t border-[var(--color-line)] px-4 py-3 lg:border-t-0">
          <div className="flex min-w-0 items-center gap-2">
            <ShoppingCart className="size-4 shrink-0 text-[var(--color-ink-soft)]" />
            <span className="truncate text-sm font-semibold">
              {resumedTicket ? resumedTicket.name : 'Current sale'}
            </span>
            {resumedTicket && <Badge tone="info">Parked</Badge>}
            {totals.itemCount > 0 && <Badge tone="brand">{totals.itemCount}</Badge>}
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <Button
              size="sm"
              variant="ghost"
              icon={<Inbox className="size-4" />}
              onClick={() => setTicketsOpen(true)}
            >
              Tickets
              {(tickets.data?.count ?? 0) > 0 && (
                <span className="tabular ml-1 rounded-full bg-crust-600 px-1.5 text-[11px] leading-tight text-white">
                  {tickets.data?.count}
                </span>
              )}
            </Button>
            {cart.length > 0 && (
              <Button size="sm" variant="ghost" icon={<Trash2 className="size-4" />} onClick={clearCart}>
                Clear
              </Button>
            )}
          </div>
        </header>

        <button
          onClick={() => setCustomerPickerOpen(true)}
          className="flex items-center gap-2 border-b border-[var(--color-line)] px-4 py-2.5 text-left text-sm hover:bg-[var(--color-surface-sunken)]"
        >
          <User className="size-4 text-[var(--color-ink-faint)]" />
          {customerName ? (
            <>
              <span className="flex-1 truncate font-medium">{customerName}</span>
              <X
                className="size-4 text-[var(--color-ink-faint)]"
                onClick={(event) => {
                  event.stopPropagation();
                  setCustomerId(null);
                  setCustomerName(null);
                }}
              />
            </>
          ) : (
            <span className="text-[var(--color-ink-faint)]">Walk-in — tap to attach a customer</span>
          )}
        </button>

        <div className="min-h-0 flex-1 overflow-y-auto">
          {cart.length === 0 ? (
            <EmptyState
              icon={<ShoppingCart className="size-8" />}
              title="No items yet"
              description="Tap a product to start the sale."
            />
          ) : (
            <ul className="divide-y divide-[var(--color-line)]">
              {totals.lines.map((line, index) => {
                const key = cart[index]!.key;
                return (
                  <li key={key} className="flex items-center gap-3 px-4 py-3">
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">{line.name}</p>
                      <p className="tabular text-xs text-[var(--color-ink-faint)]">
                        {formatMoney(line.effectiveUnitPrice)} each
                      </p>
                    </div>

                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setQty(key, line.qty - 1)}
                        aria-label="Reduce quantity"
                        className="grid size-8 place-items-center rounded-[var(--radius-sm)] border border-[var(--color-line-strong)] hover:bg-[var(--color-surface-sunken)]"
                      >
                        <Minus className="size-3.5" />
                      </button>
                      <span className="tabular w-8 text-center text-sm font-medium">{line.qty}</span>
                      <button
                        onClick={() => setQty(key, line.qty + 1)}
                        aria-label="Increase quantity"
                        className="grid size-8 place-items-center rounded-[var(--radius-sm)] border border-[var(--color-line-strong)] hover:bg-[var(--color-surface-sunken)]"
                      >
                        <Plus className="size-3.5" />
                      </button>
                    </div>

                    <span className="tabular w-20 text-right text-sm font-semibold">{formatMoney(line.lineTotal)}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* shrink-0: the cart list above gives up space, the totals never do. */}
        <footer className="shrink-0 space-y-3 border-t border-[var(--color-line)] px-4 py-3 pb-safe">
          <Select value={discountId} onChange={(event) => setDiscountId(event.target.value)}>
            <option value="">No discount</option>
            {discounts.data?.map((discount) => (
              <option key={discount.id} value={discount.id}>
                {discount.name}
              </option>
            ))}
          </Select>

          <dl className="space-y-1 text-sm">
            <Row label="Subtotal" value={formatMoney(totals.subtotal)} />
            {totals.discountAmount > 0 && (
              <Row label="Discount" value={`-${formatMoney(totals.discountAmount)}`} tone="ok" />
            )}
            <div className="flex items-baseline justify-between border-t border-[var(--color-line)] pt-2">
              <dt className="font-display text-base font-semibold">Total</dt>
              <dd className="tabular font-display text-2xl font-bold text-crust-700">{formatMoney(totals.total)}</dd>
            </div>
          </dl>

          <div className="flex gap-2">
            <Button
              size="lg"
              disabled={cart.length === 0}
              icon={<Bookmark className="size-5" />}
              onClick={() => setParkOpen(true)}
              className="shrink-0"
            >
              Park
            </Button>
            <Button
              variant="primary"
              size="lg"
              disabled={cart.length === 0}
              icon={<Banknote className="size-5" />}
              onClick={openPayment}
              className="flex-1"
            >
              Charge {formatMoney(totals.total)}
            </Button>
          </div>
        </footer>
        </div>
      </aside>

      <PaymentModal
        open={payOpen}
        onClose={() => setPayOpen(false)}
        total={totals.total}
        pending={checkout.isPending}
        shortfalls={stockCheck.data?.shortfalls ?? []}
        checking={stockCheck.isPending}
        onConfirm={(input) => checkout.mutate(input)}
      />

      <CustomerPicker
        open={customerPickerOpen}
        onClose={() => setCustomerPickerOpen(false)}
        onPick={(customer) => {
          setCustomerId(customer.id);
          setCustomerName(customer.displayName);
          setCustomerPickerOpen(false);
        }}
      />

      <ParkModal
        open={parkOpen}
        onClose={() => setParkOpen(false)}
        suggestion={resumedTicket?.name ?? customerName ?? ''}
        fallbackName={formatShopTime(new Date())}
        total={totals.total}
        itemCount={totals.itemCount}
        pending={park.isPending}
        onPark={(name) => park.mutate(name)}
      />

      <TicketsModal
        open={ticketsOpen}
        onClose={() => setTicketsOpen(false)}
        tickets={tickets.data?.tickets ?? []}
        loading={tickets.isLoading}
        activeId={resumedTicket?.id ?? null}
        cartHasItems={cart.length > 0}
        onResume={(id) => resume.mutate(id)}
        onDiscard={(id) => discard.mutate(id)}
      />
    </div>
  );
}

/**
 * Naming is the whole point of a parked ticket — an unnamed one is impossible
 * to find again at a busy counter, so the name is required.
 */
function ParkModal({
  open,
  onClose,
  suggestion,
  fallbackName,
  total,
  itemCount,
  pending,
  onPark,
}: {
  open: boolean;
  onClose: () => void;
  suggestion: string;
  fallbackName: string;
  total: number;
  itemCount: number;
  pending: boolean;
  onPark: (name: string) => void;
}) {
  /*
   * Pre-filled and selected, so parking is one keystroke when there is a queue
   * and nothing memorable to call the sale. Typing over it costs nothing, and
   * the clock is at least a name — an unnamed ticket is unfindable.
   */
  const [name, setName] = useState(suggestion || fallbackName);

  useEffect(() => {
    if (open) setName(suggestion || fallbackName);
  }, [open, suggestion, fallbackName]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="sm"
      title="Park this sale"
      description={`${itemCount} item${itemCount === 1 ? '' : 's'}, ${formatMoney(total)} — held until you come back to it.`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={pending} disabled={!name.trim()} onClick={() => onPark(name.trim())}>
            Park it
          </Button>
        </>
      }
    >
      <Field label="Name it" hint="Whatever you can shout across the counter: “blue shirt”, “Maria phone order”.">
        <Input
          autoFocus
          onFocus={(event) => event.target.select()}
          value={name}
          onChange={(event) => setName(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && name.trim()) onPark(name.trim());
          }}
          placeholder="Blue shirt"
        />
      </Field>

      <p className="mt-3 text-xs text-[var(--color-ink-faint)]">
        Nothing is charged and no stock moves until you charge it. A ticket you never
        come back to costs nothing.
      </p>
    </Modal>
  );
}

function TicketsModal({
  open,
  onClose,
  tickets,
  loading,
  activeId,
  cartHasItems,
  onResume,
  onDiscard,
}: {
  open: boolean;
  onClose: () => void;
  tickets: OpenTicket[];
  loading: boolean;
  activeId: string | null;
  cartHasItems: boolean;
  onResume: (id: string) => void;
  onDiscard: (id: string) => void;
}) {
  const [confirming, setConfirming] = useState<string | null>(null);

  return (
    <Modal open={open} onClose={onClose} title="Parked tickets" size="md">
      {loading ? (
        <Skeleton className="h-32" />
      ) : tickets.length === 0 ? (
        <EmptyState
          icon={<Bookmark className="size-8" />}
          title="Nothing parked"
          description="Use Park to set a sale aside and serve someone else. It stays here until you charge or discard it."
        />
      ) : (
        <ul className="divide-y divide-[var(--color-line)]">
          {tickets.map((ticket) => {
            // A ticket sitting since yesterday is usually forgotten, not waiting.
            const ageHours = (Date.now() - Date.parse(ticket.updatedAt)) / 3_600_000;

            return (
              <li key={ticket.id} className="flex items-center gap-3 py-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="truncate text-sm font-medium">{ticket.name}</span>
                    {ticket.id === activeId && <Badge tone="info">Open now</Badge>}
                    {ageHours > 12 && <Badge tone="warn">{Math.round(ageHours)}h old</Badge>}
                  </div>
                  <p className="truncate text-xs text-[var(--color-ink-faint)]">
                    {ticket.itemCount} item{ticket.itemCount === 1 ? '' : 's'} · {relativeTime(ticket.updatedAt)}
                    {ticket.employeeName && ` · ${ticket.employeeName}`}
                    {ticket.customerNameSnapshot && ` · ${ticket.customerNameSnapshot}`}
                  </p>
                </div>

                <span className="tabular shrink-0 text-sm font-semibold">{formatMoney(ticket.total)}</span>

                {confirming === ticket.id ? (
                  <div className="flex shrink-0 gap-1">
                    <Button size="sm" variant="danger" onClick={() => { onDiscard(ticket.id); setConfirming(null); }}>
                      Discard
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setConfirming(null)}>
                      Keep
                    </Button>
                  </div>
                ) : (
                  <div className="flex shrink-0 gap-1">
                    <Button size="sm" variant="secondary" onClick={() => onResume(ticket.id)}>
                      Open
                    </Button>
                    <button
                      onClick={() => setConfirming(ticket.id)}
                      aria-label={`Discard ${ticket.name}`}
                      className="px-1 text-[var(--color-ink-faint)] hover:text-[var(--color-danger)]"
                    >
                      <Trash2 className="size-4" />
                    </button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {cartHasItems && tickets.length > 0 && (
        <p className="mt-3 rounded-[var(--radius-md)] bg-[var(--color-warn-soft)] px-3 py-2 text-xs text-[var(--color-warn)]">
          Opening a ticket replaces what is in the cart now. Park the current sale first if you need it.
        </p>
      )}
    </Modal>
  );
}

function Row({ label, value, tone }: { label: string; value: string; tone?: 'ok' }) {
  return (
    <div className="flex justify-between">
      <dt className="text-[var(--color-ink-soft)]">{label}</dt>
      <dd className={cx('tabular', tone === 'ok' && 'text-[var(--color-ok)]')}>{value}</dd>
    </div>
  );
}

function CategoryChip({
  active,
  color,
  onClick,
  children,
}: {
  active: boolean;
  color?: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      style={active && color ? { backgroundColor: color, borderColor: color } : undefined}
      className={cx(
        'shrink-0 rounded-full border px-3.5 py-1.5 text-sm font-medium whitespace-nowrap transition',
        active
          ? color
            ? 'text-white'
            : 'border-crust-600 bg-crust-600 text-white'
          : 'border-[var(--color-line-strong)] bg-[var(--color-surface)] text-[var(--color-ink-soft)] hover:border-crust-400',
      )}
    >
      {children}
    </button>
  );
}

/**
 * Cash is the common case, so the keypad opens on it and offers the notes a
 * Manila customer actually hands over. Change is shown before the sale commits.
 */
function PaymentModal({
  open,
  onClose,
  total,
  pending,
  shortfalls,
  checking,
  onConfirm,
}: {
  open: boolean;
  onClose: () => void;
  total: number;
  pending: boolean;
  shortfalls: StockShortfallRow[];
  checking: boolean;
  onConfirm: (input: { method: string; tendered: number; reference: string | null }) => void;
}) {
  const [method, setMethod] = useState<string>('cash');
  const [tendered, setTendered] = useState(total);
  const [reference, setReference] = useState('');

  // The modal is mounted before the cart has a total, so seed the tendered
  // amount each time it opens rather than only on first render. Exact payment
  // is by far the commonest case; the keypad is there for when it is not.
  useEffect(() => {
    if (open) {
      setTendered(total);
      setReference('');
    }
  }, [open, total]);

  const change = Math.max(tendered - total, 0);
  const short = method === 'cash' && tendered < total;

  // Round up to the notes most likely to be handed over for this total.
  const quickAmounts = [total, 20000, 50000, 100000, 200000, 500000, 100000_0].filter(
    (amount, index, all) => amount >= total && all.indexOf(amount) === index,
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Take payment"
      description={`${formatMoney(total)} due`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={pending}
            disabled={short}
            icon={<Printer className="size-4" />}
            onClick={() => onConfirm({ method, tendered, reference: reference || null })}
          >
            Complete &amp; print
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {!checking && shortfalls.length > 0 && <StockWarning shortfalls={shortfalls} />}

        <div className="grid grid-cols-3 gap-2">
          {PAYMENT_METHODS.map((option) => (
            <button
              key={option.value}
              onClick={() => {
                setMethod(option.value);
                if (option.value !== 'cash') setTendered(total);
              }}
              className={cx(
                'rounded-[var(--radius-md)] border px-3 py-3 text-sm font-medium transition',
                method === option.value
                  ? 'border-crust-600 bg-[var(--color-accent-soft)] text-[var(--color-accent-ink)]'
                  : 'border-[var(--color-line-strong)] hover:bg-[var(--color-surface-sunken)]',
              )}
            >
              {option.label}
            </button>
          ))}
        </div>

        {method === 'cash' ? (
          <>
            <Field label="Cash tendered">
              <MoneyInput value={tendered} onChange={setTendered} />
            </Field>

            <div className="flex flex-wrap gap-2">
              {quickAmounts.slice(0, 5).map((amount) => (
                <button
                  key={amount}
                  onClick={() => setTendered(amount)}
                  className="tabular rounded-[var(--radius-md)] border border-[var(--color-line-strong)] px-3 py-2 text-sm hover:bg-[var(--color-surface-sunken)]"
                >
                  {formatMoney(amount, { compactWhole: true })}
                </button>
              ))}
            </div>

            <div
              className={cx(
                'flex items-baseline justify-between rounded-[var(--radius-md)] px-4 py-3',
                short ? 'bg-[var(--color-danger-soft)]' : 'bg-[var(--color-ok-soft)]',
              )}
            >
              <span className="text-sm font-medium">{short ? 'Still short' : 'Change'}</span>
              <span
                className={cx(
                  'tabular font-display text-2xl font-bold',
                  short ? 'text-[var(--color-danger)]' : 'text-[var(--color-ok)]',
                )}
              >
                {formatMoney(short ? total - tendered : change)}
              </span>
            </div>
          </>
        ) : (
          <Field label="Reference number" hint="GCash or Maya reference, bank trace, or card approval code">
            <Input value={reference} onChange={(event) => setReference(event.target.value)} placeholder="e.g. 8842 1190 33" />
          </Field>
        )}
      </div>
    </Modal>
  );
}

/**
 * Tells the cashier this sale takes something below zero.
 *
 * It does not stand in the way. The bread is on the shelf; the ledger being
 * wrong about the flour is not the customer's problem, and a register that
 * refuses a sale it can physically make is worse than a number that has drifted.
 *
 * The two cases read differently on purpose. Running out is a buying problem.
 * Already-negative is a bookkeeping one, and telling someone to buy flour when
 * the real fault is an unrecorded delivery wastes their morning.
 */
function StockWarning({ shortfalls }: { shortfalls: StockShortfallRow[] }) {
  const drift = shortfalls.filter((s) => s.recordsAlreadyWrong);
  const running = shortfalls.filter((s) => !s.recordsAlreadyWrong);

  const round = (value: number) => Math.round(value * 1000) / 1000;

  return (
    <div className="rounded-[var(--radius-md)] border border-[var(--color-warn)] bg-[var(--color-warn-soft)] p-3">
      <div className="flex items-start gap-2">
        <AlertTriangle className="mt-0.5 size-4 shrink-0 text-[var(--color-warn)]" />
        <div className="min-w-0 space-y-2 text-sm">
          {running.length > 0 && (
            <div>
              <p className="font-medium text-[var(--color-warn)]">This sale uses more than you have</p>
              <ul className="mt-1 space-y-0.5 text-[var(--color-ink-soft)]">
                {running.map((s) => (
                  <li key={s.id}>
                    <span className="font-medium">{s.name}</span> — {round(s.onHandDisplay)} {s.stockUnit} left,
                    needs {round(s.requiredDisplay)} {s.stockUnit}
                    <span className="text-[var(--color-ink-faint)]"> ({s.drivenBy.join(', ')})</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {drift.length > 0 && (
            <div>
              <p className="font-medium text-[var(--color-warn)]">
                {running.length > 0 ? 'Also: your records disagree with the shelf' : 'Your records disagree with the shelf'}
              </p>
              <ul className="mt-1 space-y-0.5 text-[var(--color-ink-soft)]">
                {drift.map((s) => (
                  <li key={s.id}>
                    <span className="font-medium">{s.name}</span> is already at {round(s.onHandDisplay)}{' '}
                    {s.stockUnit} — likely a delivery that was never keyed in
                  </li>
                ))}
              </ul>
            </div>
          )}

          <p className="text-xs text-[var(--color-ink-faint)]">
            Sell anyway if the goods are there. Fix the count in Stock room afterwards.
          </p>
        </div>
      </div>
    </div>
  );
}

function CustomerPicker({
  open,
  onClose,
  onPick,
}: {
  open: boolean;
  onClose: () => void;
  onPick: (customer: { id: string; displayName: string }) => void;
}) {
  const [search, setSearch] = useState('');
  const customers = useQuery({
    queryKey: ['customers', search],
    queryFn: () => api.customers.list({ search: search || undefined, limit: 30 }),
    enabled: open,
  });

  return (
    <Modal open={open} onClose={onClose} title="Attach a customer" size="sm">
      <div className="space-y-3">
        <Input
          autoFocus
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Name, phone or @handle…"
        />
        <ul className="divide-y divide-[var(--color-line)]">
          {customers.data?.customers.map((customer) => (
            <li key={customer.id}>
              <button
                onClick={() => onPick(customer)}
                className="flex w-full items-center justify-between gap-3 py-2.5 text-left hover:bg-[var(--color-surface-sunken)]"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">{customer.displayName}</p>
                  <p className="truncate text-xs text-[var(--color-ink-faint)]">{customer.phone ?? customer.code}</p>
                </div>
                {customer.orderCount > 0 && (
                  <span className="tabular shrink-0 text-xs text-[var(--color-ink-faint)]">
                    {customer.orderCount} orders
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
        {customers.data?.customers.length === 0 && (
          <p className="py-6 text-center text-sm text-[var(--color-ink-faint)]">No customer matches that.</p>
        )}
      </div>
    </Modal>
  );
}

export type { NotifyResult, ReceiptPayload };
